import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useToast } from '@/components/ui';
import { RECORDS, type SampleRecord } from './model';
import { RecordsContext, type RecordPatch } from './recordsContext';

const KEY = 'kaname.patterns.records.v1';
function load(): SampleRecord[] {
  try {
    const saved: Record<string, RecordPatch> = JSON.parse(localStorage.getItem(KEY) ?? '{}');
    return RECORDS.map((record) => {
      const patch = saved[record.id];
      if (!patch || typeof patch !== 'object') return record;
      return {
        ...record,
        title: typeof patch.title === 'string' && patch.title.trim() ? patch.title : record.title,
        note: typeof patch.note === 'string' ? patch.note : record.note,
        status:
          patch.status && ['new', 'review', 'paid'].includes(patch.status) ? patch.status : record.status,
      };
    });
  } catch {
    return RECORDS;
  }
}

export function RecordsProvider({ children }: { children: ReactNode }) {
  const [records, setRecords] = useState(load);
  const { toast } = useToast();
  useEffect(() => {
    try {
      localStorage.setItem(
        KEY,
        JSON.stringify(
          Object.fromEntries(records.map((r) => [r.id, { title: r.title, note: r.note, status: r.status }])),
        ),
      );
    } catch {
      /* Образец работает и без сохранения. */
    }
  }, [records]);

  const update = useCallback(
    (id: string, patch: RecordPatch) => {
      const before = records.find((r) => r.id === id);
      if (!before) return;
      setRecords((current) => current.map((r) => (r.id === id ? { ...r, ...patch } : r)));
      toast({
        title: 'Изменения сохранены',
        action: {
          label: 'Отменить',
          undo: true,
          onClick: () =>
            setRecords((current) =>
              current.map((r) => {
                if (r.id !== id) return r;
                const restored = { ...r };
                if (patch.title !== undefined && r.title === patch.title) restored.title = before.title;
                if (patch.note !== undefined && r.note === patch.note) restored.note = before.note;
                if (patch.status !== undefined && r.status === patch.status) restored.status = before.status;
                return restored;
              }),
            ),
        },
      });
    },
    [records, toast],
  );

  const changeStatus = useCallback(
    (ids: readonly string[], value: SampleRecord['status']) => {
      const before = new Map(
        records.filter((r) => ids.includes(r.id) && r.status !== value).map((r) => [r.id, r.status]),
      );
      if (!before.size) return;
      setRecords((current) => current.map((r) => (before.has(r.id) ? { ...r, status: value } : r)));
      toast({
        title: before.size === 1 ? 'Статус изменён' : `Изменён статус записей: ${before.size}`,
        action: {
          label: 'Отменить',
          undo: true,
          onClick: () =>
            setRecords((current) =>
              current.map((r) =>
                before.has(r.id) && r.status === value ? { ...r, status: before.get(r.id)! } : r,
              ),
            ),
        },
      });
    },
    [records, toast],
  );

  const api = useMemo(() => ({ records, update, changeStatus }), [records, update, changeStatus]);
  return <RecordsContext.Provider value={api}>{children}</RecordsContext.Provider>;
}
